import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';

import { EventService } from '../../../services/event.service';
import { SupplierService } from '../../../services/supplier.service';
import { SupplierFeedbackService } from '../../../services/supplier-feedback.service';
import { ISupplierFeedback } from '../../../interfaces/supplier-feedback/supplier-feedback';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng';
import { Message } from '../../../classes/Message';
import { ISupplierDetailedInfo } from '../../../interfaces/telephony/supplier-detailed-info';
import { SupplierInfoEventTypeEnum } from '../../../interfaces/telephony/supplier-info-event-type.enum';
import { OrganisationInteractionService } from '../../../services/organisation-interaction.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TelephonyEventEnum } from '../../../interfaces/telephony/telephony-event';
import { ISupplierInfoEvent } from '../../../interfaces/telephony/supplier-info-event';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { compare } from 'fast-json-patch';
import { OrganisationsService, TelephonyService, UserService } from '../../../services';
import { IJobTaskInfo } from '../../../interfaces/telephony/job-task-info';
import { IJobCardInfo } from '../../../interfaces/telephony/job-card-info';
import { IOrganisationPhoneNumber } from '../../../interfaces/telephony/organisation-phone-number';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SupplierAvailabilityStateEnum } from '../../../interfaces/supplier-schedule';

@Component({
  selector: 'supplier-information',
  templateUrl: './supplier-information.component.html',
  styleUrls: ['./supplier-information.component.scss'],
})
export class SupplierInformationComponent implements OnInit, OnDestroy {
  public editForm: UntypedFormGroup;
  private preEdit: any;
  private preEditPhoneNumbers: any;
  private toDelete: number[] = [];

  constructor(
    private eventService: EventService,
    private supplierService: SupplierService,
    private organisationService: OrganisationsService,
    private supplierFeedbackService: SupplierFeedbackService,
    private messageService: MessageService,
    private interactionService: OrganisationInteractionService,
    private route: ActivatedRoute,
    private router: Router,
    public telephonyService: TelephonyService,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef,
    private userService: UserService,
  ) {}

  supplierInfoVisible = false;
  supplierInfo: ISupplierDetailedInfo;
  supplierHomePos: { latitude: number; longitude: number };

  liveJobs: IJobCardInfo[];
  upcomingJobs: IJobCardInfo[];

  loadingData = false;
  loadingFeedback = false;
  supplierFeedback: ISupplierFeedback[];

  supplierInfoEventSub: Subscription;

  loadingJobScreen = false;
  impersonating = false;
  isImpersonating: boolean = false;

  supplierId;
  showEditContactDetails: boolean = false;
  phoneNumberList: string[] = [];
  isCheckingNumber = false;
  originalNumbers: string[] = [];

  activeIndex = 0;
  showImpersonateDialog: boolean;
  supplierIsDND = false;

  async ngOnInit() {
    // this.telephonyUserSub = this.eventService.TelephonyUserLoaded.subscribe((user) => {
    //   this.impersonating = user.isImpersonating;
    // });

    this.activeIndex = 1;

    this.route.params.subscribe((params: Params) => {
      this.supplierId = params['id'];
      this.activeIndex = 1;

      this.loadingJobScreen = false;
      this.refreshSupplierInfo();
    });

    this.supplierInfoEventSub = this.eventService.SupplierInfoEvent.subscribe(
      (event: ISupplierInfoEvent) => {
        switch (event.eventType) {
          case SupplierInfoEventTypeEnum.CloseInfoDialog:
            this.supplierInfoVisible = false;
            break;
          case SupplierInfoEventTypeEnum.OpenInfoDialog:
            this.loadingJobScreen = false;

            // if we've been passed a number then we need to load supplier information
            // (this occurs when an unknown number has been associated with an organisation
            if (event.args) {
              this.loadSupplierInformationFromNumber(event.args);
            }

            this.supplierInfoVisible = true;
            break;
        }
      },
    );

    this.initEditForm();

    this.isImpersonating = (await this.userService.isUserImpersonating()).isImpersonating;
  }

  ngOnDestroy() {
    if (this.supplierInfoEventSub) {
      this.supplierInfoEventSub.unsubscribe();
    }
  }

  refreshSupplierInfo() {
    this.loadingData = true;

    this.loadSupplierInformationFromId(this.supplierId).then((ok) => {
      if (ok) {
        this.supplierInfoVisible = true;
        this.loadingData = false;
      }
    });
  }

  async loadSupplierInformationFromNumber(telephoneNumber): Promise<boolean> {
    await this.supplierService
      .getDetailedInformationFromNumber(telephoneNumber)
      .then((info) => {
        this.supplierInfo = info;
      })
      .catch((err) => {
        this.messageService.add(
          Message.Error('Load Supplier Info', 'An error occurred loading supplier information'),
        );
        return Promise.resolve(false);
      });

    if (!this.supplierInfo) {
      return Promise.resolve(false);
    }

    await this.setSupplierInfo(this.supplierInfo);
    return Promise.resolve(true);
  }

  async setPhoneNumberList(info: ISupplierDetailedInfo) {
    let phoneNumbers: string[] = [];

    phoneNumbers = info.organisationPhoneNumbers.map((phoneNumber) => {
      return phoneNumber.phoneNumber;
    });
    phoneNumbers.push(info.telephone);

    // Create unique list of phone numbers
    phoneNumbers = phoneNumbers.filter((phoneNumber, index, self) => {
      return index === self.indexOf(phoneNumber);
    });

    this.phoneNumberList = phoneNumbers;
  }

  /**
   * Conservative normalization for tel: URIs.
   * Keeps digits and a leading "+"; strips spaces, brackets, dashes, etc.
   */
  toTelHref(phone: string): string {
    const raw = (phone ?? '').trim();
    if (!raw) return '';

    const hasLeadingPlus = raw.startsWith('+');
    const digitsOnly = raw.replace(/[^\d]/g, '');
    const normalized = (hasLeadingPlus ? '+' : '') + digitsOnly;

    return normalized ? `tel:${normalized}` : '';
  }

  async loadSupplierInformationFromId(id: number): Promise<boolean> {
    await this.supplierService
      .getDetailedInformationFromId(id)
      .then((info) => {
        this.supplierInfo = info;
        this.setPhoneNumberList(info);
      })
      .catch((err) => {
        this.messageService.add(
          Message.Error('Load Supplier Info', 'An error occurred loading supplier information'),
        );
        return Promise.resolve(false);
      });

    if (!this.supplierInfo) {
      return Promise.resolve(false);
    }

    await this.setSupplierInfo(this.supplierInfo);
    return Promise.resolve(true);
  }

  async setSupplierInfo(information: ISupplierDetailedInfo) {
    this.supplierInfo = information;
    if (!information) {
      this.messageService.add(Message.Error('Load Supplier Info', 'Supplier was not found'));

      // raise info loaded event for interested components (false means loading was unsuccessful)
      this.eventService.SupplierInfoEvent.emit({
        eventType: SupplierInfoEventTypeEnum.InfoLoaded,
        args: false,
      });
      return;
    }

    //console.log("Recent calls: ", information.recentCalls);

    this.supplierHomePos = {
      latitude: this.supplierInfo.latitude,
      longitude: this.supplierInfo.longitude,
    };

    // filter out current jobs by status (6 = started, 7 = paused)
    this.liveJobs = this.supplierInfo.supplierJobs.filter(
      (x) => x.statusId == 6 || x.statusId == 7,
    );

    // 5 = Created
    this.upcomingJobs = this.supplierInfo.supplierJobs.filter((x) => x.statusId == 5);
    this.upcomingJobs.sort((a, b) => {
      const minDateA = this.getMinimumPickupDate(a.tasks);
      const minDateB = this.getMinimumPickupDate(b.tasks);
      return +minDateA - +minDateB;
    });

    // raise info loaded event for interested components (true means loading was successful)
    this.eventService.SupplierInfoEvent.emit({
      eventType: SupplierInfoEventTypeEnum.InfoLoaded,
      args: true,
    });

    this.loadingFeedback = true;
    await this.supplierFeedbackService.getRecent(this.supplierInfo.id, 20).then((result) => {
      this.supplierFeedback = result;
      //console.log("Feedback: ", this.supplierFeedback);
    });

    this.loadingFeedback = false;
  }

  private getMinimumPickupDate(tasks: IJobTaskInfo[]): Date {
    return tasks.length === 0
      ? new Date(0)
      : new Date(Math.min(...tasks.map((task) => new Date(task.pickupEarliestDateTime).getTime())));
  }

  initEditForm() {
    this.editForm = new FormGroup({
      id: new FormControl(),
      addressLine1: new FormControl('', Validators.required),
      addressLine2: new FormControl('', Validators.required),
      addressLine3: new FormControl('', Validators.required),
      city: new FormControl('', Validators.required),
      town: new FormControl('', Validators.required),
      postcode: new FormControl('', Validators.required),
      phoneNumbers: new FormArray([]),
      phoneNumber: new FormControl('', Validators.required),
      emailAddress: new FormControl('', Validators.required),
    });
  }

  closeDialogWithoutSave() {
    this.showEditContactDetails = false;
    this.toDelete = [];
  }

  async saveEditForm() {
    if (this.isCheckingNumber) {
      return;
    }
    // Get the patch
    let patch = compare(this.preEdit, this.f);

    // Isolate the phone number patches
    let phonePatches = patch.filter((x) => x.path.startsWith('/phoneNumbers'));

    this.toDelete.forEach((x) => {
      // Delete the phone number
      this.telephonyService.deleteTelephoneNumber(this.supplierId, x).then((result) => {});
    });

    phonePatches.forEach((x) => {
      const arrayItem = parseInt(x.path.split('/')[2]);

      if (x.op == 'add') {
        let dto = {
          organisationId: this.f.id,
          phoneNumber: x.value.telephone,
        } as IOrganisationPhoneNumber;

        // Add the phone number
        this.telephonyService.setOrganisationPhoneNumber(dto).then((result) => {});
      }

      if (x.op == 'replace') {
        const organisationPhoneNumberId = this.f.phoneNumbers[arrayItem].id;

        // Replace the phone number
        this.telephonyService
          .updateOrganisationPhoneNumber(organisationPhoneNumberId, x.value)
          .then((result) => {});
      }
    });

    let preData = Object.assign({}, this.preEdit);
    delete preData.phoneNumbers;

    let newData = Object.assign({}, this.f);
    delete newData.phoneNumbers;

    await this.supplierService.patch(this.f.id, newData, preData).then((result) => {
      this.loadSupplierInformationFromId(this.supplierId).then((x) => {
        this.showEditContactDetails = false;
      });
    });
  }

  get f() {
    return this.editForm.value;
  }

  initEditContactDetails() {
    this.showEditContactDetails = true;

    this.editForm.patchValue({
      id: this.supplierInfo.id,
      addressLine1: this.supplierInfo.addressLine1,
      addressLine2: this.supplierInfo.addressLine2,
      addressLine3: this.supplierInfo.addressLine3,
      city: this.supplierInfo.city,
      town: this.supplierInfo.town,
      postcode: this.supplierInfo.postcode,
      telephoneNumber: this.supplierInfo.telephone,
      phoneNumbers: [],
      emailAddress: this.supplierInfo.emailAddress,
    });

    this.phoneNumbers.clear();

    this.originalNumbers = [
      ...this.supplierInfo.organisationPhoneNumbers.map((n) => {
        return n.phoneNumber;
      }),
    ];

    this.supplierInfo.organisationPhoneNumbers.forEach((phone) => {
      this.addNewPhone(phone.id, phone.phoneNumber);
    });

    // Add the telephone number if it's not already in the list

    this.preEdit = Object.assign({}, this.editForm.value);
  }

  get phoneNumbers() {
    return this.editForm.controls['phoneNumbers'] as FormArray;
  }

  async addNewPhone(id = null, phoneNum: string = '') {
    // Don't allow us to create more than one blank line
    if (this.phoneNumbers.controls.some((control) => control?.value?.telephone?.trim() === '')) {
      return;
    }

    this.phoneNumbers.push(
      this.fb.group({
        id: new FormControl(id),
        telephone: new FormControl(phoneNum),
      }),
    );
  }

  deletePhone(index) {
    if (this.phoneNumbers.controls[index].value.id) {
      this.toDelete.push(this.phoneNumbers.controls[index].value.id);
    }

    this.phoneNumbers.removeAt(index);
  }

  toggleAISummary(call: any) {}

  async onPhoneNumberAdded(index, event: FocusEvent) {
    // ignore validation check if we're closing the dialog without saving
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (relatedTarget?.getAttribute('data-action') === 'cancel') {
      return;
    }

    const phoneNumber = this.phoneNumbers.at(index).get('telephone').value;

    console.log('Checking phone number: ', phoneNumber);

    // check and confirm if this will overwrite existing number
    this.isCheckingNumber = true;
    const canProceed = await this.telephonyService.checkExistingNumberAssignment(
      phoneNumber,
      null,
      this.supplierId,
    );
    this.isCheckingNumber = false;

    if (!canProceed) {
      // remove the form control if it wasn't an update to an existing number, otherwise revert the number
      const isExistingNumber = index < this.originalNumbers.length;

      if (isExistingNumber) {
        // Revert to original number
        this.phoneNumbers.at(index).get('telephone').setValue(this.originalNumbers[index]);
      } else {
        // Remove only if it's a new number
        this.phoneNumbers.removeAt(index);
      }

      this.cdRef.detectChanges();

      return;
    }

    this.cdRef.detectChanges();
  }

  impersonateSupplier() {
    this.showImpersonateDialog = true;
  }

  onImpersonateClose() {
    this.showImpersonateDialog = false;
  }

  handleSupplierTodayDNDChanged(event: boolean) {
    this.supplierIsDND = event;
  }
}
